import { getAnalyticsConfig } from "./config";
import { getAnalyticsContext } from "./context";
import { createQueue } from "./queue";
import { getSessionId, touchSession } from "./session";
import { hasAnalyticsAuth } from "./auth";

let initialized = false;
let queue = null;
let config = null;
let lastPage = null;
let errorHandlersBound = false;

const buildEvent = (eventName, metadata = {}, pageOverride) => {
  const ctx = getAnalyticsContext();
  const meta =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? { ...metadata }
      : {};

  const companyId =
    meta.companyId != null ? String(meta.companyId) : ctx.companyId;
  const unitId = meta.unitId != null ? String(meta.unitId) : ctx.unitId;
  delete meta.companyId;
  delete meta.unitId;

  const page =
    pageOverride ||
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : null);

  const event = {
    applicationId: config.applicationId,
    event: eventName,
    companyId,
    unitId,
    sessionId: getSessionId(config.idleMs),
    metadata: meta,
  };

  if (page) {
    event.page = page;
  }

  return event;
};

const bindErrorHandlers = () => {
  if (errorHandlersBound || typeof window === "undefined") return;
  errorHandlersBound = true;

  window.addEventListener("error", (event) => {
    track("frontend_error", {
      message: String(event?.message || "error").slice(0, 500),
      url:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search || ""}`
          : null,
      stack: String(event?.error?.stack || "").slice(0, 1000),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const message =
      typeof reason === "string"
        ? reason
        : reason?.message || "unhandledrejection";

    track("frontend_error", {
      message: String(message).slice(0, 500),
      url:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search || ""}`
          : null,
      type: "unhandledrejection",
    });
  });

  window.addEventListener("beforeunload", () => {
    if (queue) queue.flush({ keepalive: true });
  });
};

export const init = () => {
  if (initialized) return;

  config = getAnalyticsConfig();
  if (!config.enabled) {
    initialized = true;
    return;
  }

  queue = createQueue({
    endpoint: config.endpoint,
    maxBatch: config.maxBatch,
    flushIntervalMs: config.flushIntervalMs,
  });

  bindErrorHandlers();
  initialized = true;
};

export const track = (eventName, metadata = {}) => {
  if (!initialized) init();
  if (!config?.enabled || !queue || !eventName || !hasAnalyticsAuth()) return;

  touchSession();
  queue.enqueue(buildEvent(eventName, metadata));
};

export const page = (path) => {
  if (!initialized) init();
  if (!config?.enabled || !queue || !hasAnalyticsAuth()) return;

  const pagePath =
    path ||
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : null);

  if (!pagePath || pagePath === lastPage) return;
  lastPage = pagePath;

  touchSession();
  queue.enqueue(
    buildEvent(
      "page_view",
      {
        referrer:
          typeof document !== "undefined" ? document.referrer || null : null,
      },
      pagePath
    )
  );
};

export const flush = () => {
  if (queue) return queue.flush();
  return Promise.resolve();
};
